import { ChevronUp, ChevronDown } from "lucide-react";
import Input from "../../../Shared/Input";
import TextArea from "../../../Shared/TextArea";
import TrashIcon from "../../../Icons/TrashIcon";
import { CBQQuestionType } from "../../../../api/Quiz/InitCBQQuestion";
import { QuestionTypeEditorProps } from "../../CBQExams/editors/types";
import McqEditor from "../../CBQExams/editors/McqEditor";
import MultiResponseEditor from "../../CBQExams/editors/MultiResponseEditor";
import OrderingEditor from "../../CBQExams/editors/OrderingEditor";
import ClassificationEditor from "../../CBQExams/editors/ClassificationEditor";
import DropdownEditor from "../../CBQExams/editors/DropdownEditor";
import NumericEditor from "../../CBQExams/editors/NumericEditor";
import { DraftQuestion } from "./types";

const TYPE_LABELS: Record<CBQQuestionType, string> = {
  mcq: "Multiple Choice",
  multi_response: "Multiple Response",
  drag_drop_ordering: "Drag & Drop — Ordering",
  drag_drop_classification: "Drag & Drop — Classification",
  dropdown: "Dropdown / Fill-in",
  numeric: "Numeric",
};

const EDITORS: Record<
  CBQQuestionType,
  (props: QuestionTypeEditorProps) => JSX.Element
> = {
  mcq: McqEditor,
  multi_response: MultiResponseEditor,
  drag_drop_ordering: OrderingEditor,
  drag_drop_classification: ClassificationEditor,
  dropdown: DropdownEditor,
  numeric: NumericEditor,
};

interface LocalQuestionEditorProps {
  index: number;
  total: number;
  value: DraftQuestion;
  onChange: (next: DraftQuestion) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}

const LocalQuestionEditor = ({
  index,
  total,
  value,
  onChange,
  onRemove,
  onMove,
}: LocalQuestionEditorProps) => {
  const TypeEditor = EDITORS[value.type];

  return (
    <div className="rounded-xl border border-black/10 border-l-[3px] border-l-[#4379EE] bg-white shadow-sm p-5 space-y-5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center rounded-md bg-[#4379EE] text-white text-xs font-bold px-2 py-1">
            Q{index + 1}
          </span>
          <span className="text-sm font-semibold text-burntBlack">
            {TYPE_LABELS[value.type]}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onMove(-1)}
            disabled={index === 0}
            className="p-1.5 rounded-md text-gray-400 hover:text-[#4379EE] hover:bg-[#4379EE]/5 disabled:opacity-30 disabled:hover:bg-transparent"
            title="Move up"
          >
            <ChevronUp size={16} />
          </button>
          <button
            type="button"
            onClick={() => onMove(1)}
            disabled={index === total - 1}
            className="p-1.5 rounded-md text-gray-400 hover:text-[#4379EE] hover:bg-[#4379EE]/5 disabled:opacity-30 disabled:hover:bg-transparent"
            title="Move down"
          >
            <ChevronDown size={16} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="flex rounded-lg justify-center items-center border border-black/10 p-2 hover:bg-red-50 hover:border-red-200 transition-colors"
            title="Remove question"
          >
            <TrashIcon />
          </button>
        </div>
      </div>

      <TextArea
        title="Prompt"
        name="q-prompt"
        placeholder="Question prompt"
        value={value.prompt}
        onChange={(e) => onChange({ ...value, prompt: e.target.value })}
      />
      <Input
        title="Explanation"
        name="q-help"
        placeholder="Optional hint shown to students"
        value={value.helpText}
        onChange={(e) => onChange({ ...value, helpText: e.target.value })}
      />
      <Input
        title="Points"
        name="q-points"
        type="number"
        step="any"
        placeholder="e.g. 1.5"
        value={String(value.points)}
        onChange={(e) => onChange({ ...value, points: Number(e.target.value) })}
      />

      <div className="pt-2 border-t border-black/10">
        <TypeEditor
          optionsJson={value.optionsJson}
          correctAnswerJson={value.correctAnswerJson}
          onChange={(next) =>
            onChange({
              ...value,
              optionsJson: next.optionsJson,
              correctAnswerJson: next.correctAnswerJson,
            })
          }
        />
      </div>
    </div>
  );
};

export default LocalQuestionEditor;
